import React, {
  ForwardedRef,
  useImperativeHandle,
  useRef,
} from 'react';
import Chart from '..';
import { LineChartProps, ChartRef, ChartOption } from '../_config/interface';
import { yAxisFormat } from '../_config/utils';
import { CHART_FONT_FAMILY, DARK_COLOR_ARR, LIGHT_COLOR_ARR } from '../_config/constant';
import { useIntl } from 'umi';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { getDataIndexList } from '@/pages/dashboard/components/Chart/_config/plug';
import { numFormat } from '@/pages/dashboard/utils/utils';

const StairsChartInner: React.ForwardRefRenderFunction<
ChartRef,
LineChartProps
> = (props, ref: ForwardedRef<ChartRef>) => {
  const {
    className = '',
    style = {},
    height,
    hasDataZoom,
    chartEventConfig,
    notMerge,
    isStackBar = false,
    handleLegendSelectChange,
    onLegendMouseover,
    onLegendMouseout,
  } = props;
  const chartRef = useRef<ChartRef>(null);
  const { formatMessage } = useIntl();

  const makeupOptions = (props: LineChartProps, chartType: string) => {
    const {
      formatterFunc,
      isDark,
      legendData,
      colorArr,
      series,
      xAxisData,
      setOtherConfigFunc,
      isShowLegend = true,
      isShowTooltip = true,
      barMaxWidth = 80,
    } = props;


    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const opts = {
      textStyle: {
        fontFamily: CHART_FONT_FAMILY
      },
      tooltip: {
        show: isShowTooltip,
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        borderWidth: 0,
        borderRadius: 6,
        textStyle: {
          color: '#FFFFFF',
          fontSize: 12,
        },
        axisPointer: {
          type: 'line',
        },
        confine: true,
        hideDelay: 200,
        triggerOn: 'mousemove',
        padding: 0,
        formatter: formatterFunc ? formatterFunc : null
      },
      axisPointer: {
        link: [
          {
            xAxisIndex: 'all'
          }
        ],
        label: {
          backgroundColor: '#777'
        }
      },
      grid: [{
        left: 40,
        right: 10,
        top: 20,
        bottom: '42%',
      },
      {
        left: 40,
        right: 10,
        top: '72%',
        bottom: 30,
        tooltip: {
          show: false,
          formatter: null,
        }
      }],
      legend: {
        show: isShowLegend,
        type: 'scroll',
        data: legendData,
        selectedMode: true,
        orient: 'horizontal',
        itemHeight: 8,
        itemWidth: 8,
        icon: 'circle',
        bottom: -6,
        itemGap: 18,
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end',// start
        left: 'center',
        textStyle: { // 图例文字的样式
          color: '#838697',
          fontSize: 12,
          lineHeight: 16,
        }
      },
      color: colorArr ? colorArr : isDark ? DARK_COLOR_ARR : LIGHT_COLOR_ARR,
      xAxis: [
        {
          type: 'category',
          gridIndex: 0,
          data: xAxisData,
          interval: 1,
          axisLabel: {
            color: '#838697',
            fontSize: 10,
            align: 'center',
            interval: 'auto',
          },
          axisTick: {
            show: true,
            alignWithLabel: true,
            interval: 'auto',
          },
          axisLine: {
            lineStyle: {
              color: '#D8DAE4',
            },
            show: true
          },
          axisPointer: {
            type: chartType === 'line' ? 'line' : 'shadow',
          },
          // boundaryGap: false,
          splitLine: { show: false },
          min: 'dataMin',
          max: 'dataMax',
        },
        {
          type: 'category',
          gridIndex: 1,
          data: xAxisData,
          // boundaryGap: false,
          axisLine: {
            onZero: false,
            lineStyle: {
              color: '#D8DAE4',
            },
          },
          axisTick: {
            show: false,
            alignWithLabel: true,
            interval: 'auto',
          },
          splitLine: { show: false },
          axisLabel: {
            show: false,
            color: '#838697',
            fontSize: 10,
            align: 'center',
            interval: 'auto',
          },
          min: 'dataMin',
          max: 'dataMax'
        }
      ],
      axisLabel: {
        showMinLabel: true, // 强制显示最小值
      },
      yAxis: [
        {
          type: 'value',
          name: formatMessage({ id: 'pc_monetization_revenue' }, { unit: getMonetUnit() }),
          gridIndex: 0,
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#ddd',
            },
          },
          axisLabel: {
            formatter: (value: number) => yAxisFormat(value),
            color: '#838697',
            fontSize: 12,
          },
        },
        {
          type: 'value',
          name: formatMessage({ id: 'common_discount' }),
          gridIndex: 1,
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false }
        }
      ],
      series,
      barMaxWidth,
    } as ChartOption;
    return setOtherConfigFunc ? setOtherConfigFunc(opts) : opts;
  };

  const option = makeupOptions(props, 'line');

  const calculateTotal = (event: any, isStackBar: boolean) => {
    if(!isStackBar) {
      handleLegendSelectChange?.(event, option);
      return option;
    }
    const eventSelected = event.selected;
    const optionSeries = option.series;
    const newTotalData = Array(optionSeries[0].data.length).fill(0);
    option.series.slice(0, option.series.length - 1).forEach((optionSeries: { name: string | number; data: any[]; }) => {
      // eslint-disable-next-line no-prototype-builtins
      if (eventSelected.hasOwnProperty(optionSeries.name) && eventSelected[optionSeries.name]) {
        optionSeries.data.forEach((data, index) => {
          newTotalData[index] += parseFloat(data) || 0;
        });
      }
    });
    const newFormattedTotalData = newTotalData.map(data => numFormat(data.toFixed(0)));
    const indexList = getDataIndexList(newTotalData);
    option.series[option.series.length - 1].label.formatter = (params: { dataIndex: number; }) => {
      if (indexList.includes(params.dataIndex) && newTotalData[params.dataIndex] > 0) {
        return newFormattedTotalData[params.dataIndex];
      } else {
        return '';
      }
    };
    handleLegendSelectChange?.(event, option);
    return option;
  };

  // 获取实例
  const instance = () => {
    return chartRef?.current?.instance();
  };

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
  }));

  const chartStyle = {
    ...(height ? { height: `${height}px` } : {}),
    width: '100%',
    position: 'static',
    ...style,
  };

  return (
    <div className={`chart-box ${className}`} style={chartStyle}>
      <Chart
        ref={chartRef}
        option={option}
        hasDataZoom={hasDataZoom}
        chartEventConfig={chartEventConfig}
        notMerge={notMerge}
        calculateTotal={(e: any)=>calculateTotal(e,isStackBar)}
        onLegendMouseover={onLegendMouseover}
        onLegendMouseout={onLegendMouseout}
      />
    </div>
  );
};

const StairsChart = React.forwardRef(StairsChartInner);

export default StairsChart;
